// Tour de contrôle (docs/TOWER.md) : des retours aux PR approuvées. Équipe
// de la tour ; l'approbation est réservée aux approbateurs du registre.
// Page à onglets, carte d'approbation mobile, panneau d'un retour.
import type { JSX } from 'preact'
import { useCallback, useEffect, useState } from 'preact/hooks'
import type { FeedbackItem, TowerAgents, TowerItem, TowerOverview, TowerProject, TowerTicketDetail, TowerTicketRow, TowerTriageCard } from '@feedbaacks/sdk'
import { Button, cx, errorMessage, Icons, Spinner, useFeedbaacks } from '@feedbaacks/ui'
import { href, onLinkClick } from '../router'
import { STATE_META, durationLabel, money, parseFilter, showApprovalCard, sourceLabel } from './tower-model'
import { useTowerAct, useTowerRealtime } from './tower-hooks'
import { Chip, TowerApprovalCard } from './TowerParts'
import { AgentsView, ProjectsView, RequestsView, TriageView, type RequestsQuery } from './TowerViews'
import './tower.css'

const TRIAGE_STATUS: Record<string, string> = {
  pending: 'en cours', proposed: 'proposition prête', asked: 'question posée', error: 'en échec', refused: 'refusé', ticket_created: 'ticket créé',
}

const VIEWS = [
  { key: 'demandes', label: 'Demandes' },
  { key: 'tri', label: 'À trier' },
  { key: 'agents', label: 'Agents' },
  { key: 'projets', label: 'Projets' },
] as const
type TowerView = (typeof VIEWS)[number]['key']

/** L'état de la page vit dans l'URL : un lien se partage, un rechargement
 *  le garde. `replace` pour la frappe (pas une entrée d'historique par lettre). */
function useQuery(): [URLSearchParams, (patch: Record<string, string | null>, replace?: boolean) => void] {
  const [, force] = useState(0)
  useEffect(() => {
    const on = () => force((n) => n + 1)
    window.addEventListener('popstate', on)
    return () => window.removeEventListener('popstate', on)
  }, [])
  const sp = new URLSearchParams(location.search)
  const set = (patch: Record<string, string | null>, replace = false) => {
    const next = new URLSearchParams(location.search)
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k)
      else next.set(k, v)
    }
    const qs = next.toString()
    const url = location.pathname + (qs ? `?${qs}` : '')
    if (replace) history.replaceState(null, '', url)
    else history.pushState(null, '', url)
    force((n) => n + 1)
  }
  return [sp, set]
}

/** Filtre, projet et recherche vers les paramètres f, p et q de l'URL. */
function queryPatch(patch: Partial<RequestsQuery>): Record<string, string | null> {
  const out: Record<string, string | null> = {}
  if (patch.filter !== undefined) out.f = patch.filter === 'tous' ? null : patch.filter
  if (patch.project !== undefined) out.p = patch.project
  if (patch.q !== undefined) out.q = patch.q
  return out
}

function LiveDot({ live }: { live: boolean }): JSX.Element {
  return (
    <span class="tw-live fb-muted" data-testid="tower-live">
      <span class={cx('tw-dot', live ? 'is-green' : 'is-amber')} />{live ? 'en direct' : 'actualisation périodique'}
    </span>
  )
}

export function TowerPage({ superAdmin }: { superAdmin: boolean }): JSX.Element {
  const { client, notify, confirm } = useFeedbaacks()
  const [sp, set] = useQuery()
  const raw = sp.get('view')
  const view: TowerView = VIEWS.some((v) => v.key === raw) ? (raw as TowerView) : 'demandes'
  const selected = sp.get('ticket')
  const query: RequestsQuery = { filter: parseFilter(sp.get('f')), project: sp.get('p') ?? '', q: sp.get('q') ?? '' }
  const wantArchive = query.filter === 'archives'
  const [overview, setOverview] = useState<TowerOverview | null>(null)
  const [archived, setArchived] = useState<TowerTicketRow[] | null>(null)
  const [triages, setTriages] = useState<TowerTriageCard[] | null>(null)
  const [agents, setAgents] = useState<TowerAgents | null>(null)
  const [projects, setProjects] = useState<TowerProject[]>([])
  const [isApprover, setIsApprover] = useState(false)
  const [forbidden, setForbidden] = useState(false)
  const [version, setVersion] = useState(0)

  const refresh = useCallback(() => {
    client.tower.overview().then(setOverview, () => setForbidden(true))
    client.tower.triages().then(setTriages, () => {})
    client.tower.agents().then(setAgents, () => {})
    if (wantArchive) client.tower.archived().then(setArchived, () => setArchived([]))
    // Hors du filtre, les archives se rechargeront au prochain passage.
    else setArchived(null)
    setVersion((v) => v + 1)
  }, [client, wantArchive])
  useEffect(() => {
    refresh()
  }, [refresh])
  useEffect(() => {
    client.tower.projects().then(setProjects, () => {})
    client.tower.me().then((m) => setIsApprover(m.is_approver), () => {})
  }, [client])
  const { live } = useTowerRealtime(refresh)

  if (forbidden) return <p class="dash-center fb-muted">La tour de contrôle est réservée à son équipe.</p>
  const paused = overview?.global_paused ?? false
  const togglePause = async () => {
    if (!paused && !(await confirm({
      title: 'Suspendre les lancements ?', message: 'Aucun nouveau Job ne partira, tous projets confondus. Les Jobs en cours vont à leur terme.',
      confirmLabel: 'Suspendre',
    }))) return
    client.tower.controls({ global_paused: !paused }).then(
      () => notify('success', paused ? 'Lancements repris.' : 'Lancements suspendus.'),
      (err) => notify('error', errorMessage(err, 'Action impossible')),
    ).finally(refresh)
  }
  // Les pastilles comptent ce qui attend l'équipe (ambre) ou tourne (neutre).
  const counts: Record<TowerView, number | undefined> = {
    demandes: overview?.tickets.filter((t) => t.state === 'awaiting_approval' || t.state === 'failed').length,
    tri: triages?.filter((t) => t.status !== 'asked').length, agents: agents?.running.length, projets: undefined,
  }

  return (
    <div class="tw-page" data-testid="tower-page">
      <div class="tw-toolbar">
        <h1>Tour de contrôle</h1>
        <nav class="tw-tabs" aria-label="Vues de la tour">
          {VIEWS.map((v) => (
            <button key={v.key} type="button" aria-current={view === v.key ? 'page' : undefined} data-testid={`tower-tab-${v.key}`}
              class={cx(view === v.key && 'is-active')} onClick={() => set({ view: v.key === 'demandes' ? null : v.key, ticket: null })}>
              {v.label}
              {!!counts[v.key] && <span class={cx('tw-count', v.key !== 'agents' && 'is-amber')}>{counts[v.key]}</span>}
            </button>
          ))}
        </nav>
        <span class="tw-push tw-row">
          <LiveDot live={live} />
          <Button variant={paused ? 'primary' : 'ghost'} onClick={() => void togglePause()} testId="tower-pause-button">
            {paused ? 'Reprendre les lancements' : 'Suspendre les lancements'}
          </Button>
        </span>
      </div>
      {paused && (
        <div class="tw-paused" data-testid="tower-paused-banner">
          <Icons.Alert size={14} />Lancements suspendus : aucun nouveau Job ne part tant que la tour est en pause.
        </div>
      )}
      <div class="tw-body">
        {view === 'demandes' && (
          <RequestsView overview={overview} archived={archived} query={query} selected={selected} isApprover={isApprover} version={version} onChanged={refresh}
            onSelect={(code) => set({ ticket: code }, !!selected && !!code)} onTriage={() => set({ view: 'tri', ticket: null })}
            onQuery={(patch, replace) => set(queryPatch(patch), replace)} />
        )}
        {view === 'tri' && <TriageView cards={triages} projects={projects} onChanged={refresh} />}
        {view === 'agents' && <AgentsView agents={agents} superAdmin={superAdmin} onChanged={refresh} />}
        {view === 'projets' && <ProjectsView projects={projects} />}
      </div>
    </div>
  )
}

/** Carte d'approbation sur mobile, lien des e-mails : /tower/approve/:code. */
export function TowerApprovalPage({ code }: { code: string }): JSX.Element {
  const { client } = useFeedbaacks()
  const [t, setT] = useState<TowerTicketDetail | null>(null)
  const [missing, setMissing] = useState(false)
  const [isApprover, setIsApprover] = useState(false)
  const load = useCallback(() => client.tower.ticket(code).then(setT, () => setMissing(true)), [client, code])
  const { busy, approve, sendBack } = useTowerAct(() => void load())
  useEffect(() => {
    void load()
    client.tower.me().then((m) => setIsApprover(m.is_approver), () => {})
  }, [load])
  const { live } = useTowerRealtime(() => void load())
  const back = `${href({ name: 'tower' })}?ticket=${encodeURIComponent(code)}`
  const header = (
    <header class="tw-mobile-head">
      <a href={back} aria-label="Retour à la tour" onClick={(e) => onLinkClick(e, back)}><Icons.ArrowLeft size={18} /></a>
      <h1>Tour de contrôle</h1>
      <span class="tw-push"><LiveDot live={live} /></span>
    </header>
  )
  if (missing) return <div class="tw-mobile">{header}<p class="dash-center fb-muted">Ticket introuvable. Le lien a peut-être expiré ; ouvrez la tour pour retrouver la demande.</p></div>
  if (!t) return <div class="tw-mobile">{header}<div class="dash-center"><Spinner size={24} /></div></div>
  const st = STATE_META[t.state]
  const totalSecs = (new Date(t.updated_at).getTime() - new Date(t.created_at).getTime()) / 1000
  return (
    <div class="tw-mobile" data-testid="tower-approval-page">
      {header}
      <main class="tw-stack">
        <div class="tw-section">
          <div class="tw-row"><code class="fb-mono">{t.code}</code><Chip tone={st.tone}>{st.label}</Chip></div>
          <h2>{t.title}</h2>
          <span class="fb-muted tw-small"><span class="fb-mono">{t.project}</span> · {sourceLabel(t.source, t.company)}</span>
        </div>
        {showApprovalCard(t.state) ? (
          <TowerApprovalCard compact checks={t.checks} headSha={t.head_sha} defaultBranch={t.default_branch} previewUrl={t.preview_url}
            expiresAt={t.card_expires_at} canApprove={t.can_approve} isApprover={isApprover} busy={busy}
            onApprove={() => approve(t, (url) => window.location.assign(url))} onSendBack={(note) => sendBack(t.id, note)} />
        ) : (
          <p class="tw-card fb-muted">Ce ticket n'attend pas d'approbation : il est à l'état « {st.label} ».</p>
        )}
        <section class="tw-section">
          <h3 class="tw-overline">Ce qui change</h3>
          <p>{t.summary}</p>
          <span class="fb-muted tw-small">coût {money(t.spent_usd)} · {durationLabel(totalSecs)}</span>
        </section>
      </main>
    </div>
  )
}

/** Panneau « Tour » dans le détail d'un retour : projet visé, transmission,
 *  ticket. Invisible hors de l'équipe de la tour (403). */
export function TowerItemPanel({ item }: { item: FeedbackItem }): JSX.Element | null {
  const { client } = useFeedbaacks()
  const [info, setInfo] = useState<TowerItem | null>(null)
  const load = useCallback(() => client.tower.item(item.id).then(setInfo, () => setInfo(null)), [client, item.id])
  const { busy, act } = useTowerAct(() => void load())
  useEffect(() => {
    void load()
  }, [load, item.updated_at])
  if (!info) return null
  const forwarded = !!info.forwarded_at
  return (
    <section class="tw-item-panel" data-testid="tower-item-panel">
      <h4 class="tw-overline">Tour de contrôle</h4>
      <div class="tw-row">
        <select class="fb-select" aria-label="Projet cible" value={info.target_project} disabled={busy || forwarded} data-testid="tower-target-select"
          onChange={(e) => {
            const project = e.currentTarget.value
            void act((c) => c.tower.setTarget(item.id, project), 'Projet cible enregistré.', 'Projet refusé')
          }}>
          <option value="">Aucun projet cible</option>
          {info.projects.map((p) => <option key={p.key} value={p.key}>{p.label} ({p.key})</option>)}
        </select>
        {!forwarded && (
          <Button variant="primary" disabled={busy || !info.target_project} testId="tower-forward-button"
            onClick={() => act((c) => c.tower.forward(item.id), 'Transmis à la tour : le tri commence.', 'Transmission refusée')}>
            Transmettre à la tour
          </Button>
        )}
      </div>
      {forwarded && !info.ticket && <p class="fb-muted tw-small">Transmis à la tour{info.triage ? ` · tri : ${TRIAGE_STATUS[info.triage.status] ?? info.triage.status}` : ''}.</p>}
      {(info.tickets ?? (info.ticket ? [{ ...info.ticket, project: info.target_project }] : [])).map((tk) => {
        const to = `${href({ name: 'tower' })}?ticket=${tk.code}`
        return (
          <p key={tk.id} class={cx('tw-small', tk.removed && 'fb-muted')} data-testid={`tower-item-ticket-${tk.code}`}>
            Ticket <a href={to} onClick={(e) => onLinkClick(e, to)} class="fb-mono">{tk.code}</a>
            {(info.tickets?.length ?? 0) > 1 && <> · <span class="fb-mono">{tk.project}</span></>}
            {' · '}{tk.removed ? 'retiré de la demande' : STATE_META[tk.state].label}
            {tk.pr_url && <> · <a href={tk.pr_url} target="_blank" rel="noreferrer">PR</a></>}
          </p>
        )
      })}
    </section>
  )
}
